import * as XLSX from 'xlsx';
import type { ParseResult } from './parseResult';

export type { ParseResult } from './parseResult';

// Lector del formato real de programación mensual: un archivo por mes
// ("Formato Programación RxH <Mes>") con una pestaña por campaña, nombrada con su
// cuenta analítica. El procedimiento manual que esto automatiza —y las trampas del
// formato— están en docs/CARGA_PROGRAMACION.md.
//
// Las dos trampas que obligan a leer así y no de otra forma:
//   1. La fila TOTAL está corrida una columna: los días SIEMPRE se leen de las filas
//      de tienda. La fila TOTAL solo cierra el bloque.
//   2. La grilla puede estar desalineada respecto al calendario real y la carga
//      saldría mal EN SILENCIO: por eso validarAlineacionCalendario se corre antes.

export interface BloqueTarifa {
  rateAmountCaptador: number;
  rateAmountSupervisor: number | null;
  /** Lo que declara el Excel, no lo que se calculó de la grilla. */
  totalDiasCaptadorDeclarado: number | null;
  totalDiasSupervisorDeclarado: number | null;
  tiendas: TiendaProgramada[];
}

export interface TiendaProgramada {
  /** Tal cual viene en la grilla: puede traer prefijos que la maestra no usa. */
  nombre: string;
  cadena: string;
  region: string;
  /** Días del mes marcados con 1, en orden ascendente. */
  dias: number[];
  /** La columna TT de la fila. */
  totalDeclarado: number | null;
}

export interface CampaniaProgramada {
  cuentaAnalitica: string;
  campania: string;
  cliente: string;
  marca: string;
  categoria: string;
  dinamica: string;
  coordinador: string;
  /** Deducidos de FECHA EJECUCION. */
  anio: number;
  mes: number;
  bloques: BloqueTarifa[];
}

export interface Reconciliacion {
  jornadasCalculadas: number;
  jornadasDeclaradas: number | null;
  cuadra: boolean;
  /** `indice` es la posición del bloque en `bloques` (empieza en 0). */
  porBloque: { indice: number; calculadas: number; declaradas: number | null; cuadra: boolean }[];
}

/** Nombres de pestaña que NO son campañas. */
export const HOJAS_IGNORADAS: string[] = ['Plantilla', 'Resumen Programación'];

const NOMBRES_DIA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Etiquetas de la cabecera ya normalizadas (sin tildes, en mayúsculas): el archivo
// escribe unas con tilde y otras sin ella, y cambia mayúsculas entre meses.
const ETIQUETAS = {
  fechaEjecucion: ['FECHA EJECUCION', 'FECHA DE EJECUCION'],
  cuentaAnalitica: ['CUENTA ANALITICA'],
  campania: ['CAMPANA'],
  cliente: ['CLIENTE'],
  marca: ['MARCA'],
  categoria: ['CATEGORIA'],
  dinamica: ['DINAMICA'],
  coordinador: ['COORDINADOR'],
  presupuestoSupervisor: ['PRESUPUESTO DIARIO SUPERVISOR'],
  presupuestoCaptador: ['PRESUPUESTO DIARIO CAPTADOR'],
  totalDiasSupervisor: ['TOTAL DIAS SUPERVISOR'],
  totalDiasCaptador: ['TOTAL DIAS CAPTADOR'],
} as const;

type Campo = keyof typeof ETIQUETAS;

const TODAS_LAS_ETIQUETAS = new Set<string>(Object.values(ETIQUETAS).flat());

// --- utilidades de celda ---------------------------------------------------

function textoCelda(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

const DIACRITICOS = /[\u0300-\u036f]/g;

/** Sin tildes, sin espacios repetidos, sin ':' final y en mayúsculas. */
function normalizar(t: string): string {
  return t
    .normalize('NFD').replace(DIACRITICOS, '')
    .replace(/\s+/g, ' ').trim()
    .replace(/:+$/, '').trim()
    .toUpperCase();
}

/** Número de una celda, tolerando "S/ 90", "1,234" y el punto decimal. */
function numeroCelda(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const texto = textoCelda(v).replace(/S\/\.?/gi, '').replace(/\s/g, '');
  if (texto === '') return null;
  const limpio = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(texto)
    ? texto.replace(/,/g, '')
    : texto.replace(',', '.');
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
}

// --- calendario ------------------------------------------------------------

/** Día ISO (1=lunes … 7=domingo). Mediodía UTC para que no dependa del huso del equipo. */
function isoDiaSemana(anio: number, mes: number, dia: number): number {
  const d = new Date(Date.UTC(anio, mes - 1, dia, 12, 0, 0)).getUTCDay();
  return d === 0 ? 7 : d;
}

function diasEnMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes, 0, 12, 0, 0)).getUTCDate();
}

/** FECHA EJECUCION: texto dd/mm/aaaa o aaaa-mm-dd, serial de Excel o Date. */
function parseFechaEjecucion(v: unknown): { anio: number; mes: number } | null {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return { anio: v.getFullYear(), mes: v.getMonth() + 1 };
  }
  if (typeof v === 'number' && Number.isFinite(v) && v > 1000) {
    // Serial de Excel: días desde el 30/12/1899 (la base que corrige el bisiesto de 1900).
    const f = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86400000);
    return { anio: f.getUTCFullYear(), mes: f.getUTCMonth() + 1 };
  }
  const texto = textoCelda(v);
  const dmy = texto.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmy) return { anio: Number(dmy[3]), mes: Number(dmy[2]) };
  const ymd = texto.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (ymd) return { anio: Number(ymd[1]), mes: Number(ymd[2]) };
  return null;
}

// --- lectura del libro -----------------------------------------------------

interface Ocurrencia { valor: unknown; fila: number; col: number }

interface Grilla {
  filaEncabezado: number;
  colCadena: number;
  colRegion: number;
  colTienda: number;
  colTT: number;
  /** Columna de cada día del mes, tomada de la fila de días (nunca de la fila TOTAL). */
  dias: { dia: number; col: number }[];
}

type ErrorCarga = { rowNumber: number; message: string };

function leerLibro(data: ArrayBuffer): XLSX.WorkBook {
  return XLSX.read(data, { type: 'array' });
}

function nombreReal(wb: XLSX.WorkBook, nombreHoja: string): string | null {
  if (wb.SheetNames.includes(nombreHoja)) return nombreHoja;
  const buscado = normalizar(nombreHoja);
  return wb.SheetNames.find((n) => normalizar(n) === buscado) ?? null;
}

function esIgnorada(nombre: string): boolean {
  const n = normalizar(nombre);
  return HOJAS_IGNORADAS.some((h) => normalizar(h) === n);
}

/** Matriz cruda de la pestaña: el índice i corresponde a la fila i+1 del Excel. */
function leerAoa(wb: XLSX.WorkBook, nombre: string): unknown[][] {
  return XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[nombre], {
    header: 1, defval: '', blankrows: true,
  });
}

function etiquetaDeCelda(texto: string): { campo: Campo; resto: string } | null {
  if (texto === '') return null;
  const n = normalizar(texto);
  for (const [campo, alias] of Object.entries(ETIQUETAS) as [Campo, readonly string[]][]) {
    for (const a of alias) {
      if (n === a) return { campo, resto: '' };
      // "CAMPAÑA: Impulso Makro" — etiqueta y valor en la misma celda.
      if (n.startsWith(`${a}:`)) return { campo, resto: texto.slice(texto.indexOf(':') + 1).trim() };
    }
  }
  return null;
}

/** Primer valor no vacío a la derecha. Se detiene si encuentra otra etiqueta:
 *  un campo vacío no debe robarse el nombre del siguiente bloque. */
function valorDerecha(fila: unknown[], col: number): unknown {
  for (let c = col + 1; c < fila.length; c++) {
    const texto = textoCelda(fila[c]);
    if (texto === '') continue;
    if (etiquetaDeCelda(texto) !== null || TODAS_LAS_ETIQUETAS.has(normalizar(texto))) return '';
    return fila[c];
  }
  return '';
}

/** Cabecera: pares etiqueta/valor dispersos por encima de la grilla. Los bloques de
 *  presupuesto y totales se repiten a la derecha, uno por tarifa, así que se guardan
 *  todas las ocurrencias ordenadas de izquierda a derecha. */
function leerCabecera(aoa: unknown[][], hasta: number): Map<Campo, Ocurrencia[]> {
  const mapa = new Map<Campo, Ocurrencia[]>();
  for (let i = 0; i < hasta && i < aoa.length; i++) {
    const fila = aoa[i] ?? [];
    for (let c = 0; c < fila.length; c++) {
      const m = etiquetaDeCelda(textoCelda(fila[c]));
      if (m === null) continue;
      const valor = m.resto !== '' ? m.resto : valorDerecha(fila, c);
      const lista = mapa.get(m.campo) ?? [];
      lista.push({ valor, fila: i, col: c });
      mapa.set(m.campo, lista);
    }
  }
  for (const lista of mapa.values()) lista.sort((a, b) => a.col - b.col || a.fila - b.fila);
  return mapa;
}

function esFilaEncabezadoGrilla(fila: unknown[]): boolean {
  let cadena = false;
  let region = false;
  for (const celda of fila) {
    const n = normalizar(textoCelda(celda));
    if (n === 'CADENA') cadena = true;
    if (n === 'REGION') region = true;
  }
  return cadena && region;
}

/** Localiza la fila de días (1..31) y la fila de encabezado de la grilla. */
function ubicarGrilla(aoa: unknown[][]): { grilla?: Grilla; error?: ErrorCarga } {
  let filaDias = -1;
  let dias: { dia: number; col: number }[] = [];
  for (let i = 0; i < aoa.length && filaDias === -1; i++) {
    const fila = aoa[i] ?? [];
    const numeros: { dia: number; col: number }[] = [];
    for (let c = 0; c < fila.length; c++) {
      const n = numeroCelda(fila[c]);
      if (n !== null && Number.isInteger(n) && n >= 1 && n <= 31) numeros.push({ dia: n, col: c });
    }
    const inicio = numeros.findIndex((x) => x.dia === 1);
    if (inicio === -1) continue;
    // Una fila de tienda también trae muchos 1: solo la fila de días es correlativa.
    const correlativos = [numeros[inicio]];
    for (let k = inicio + 1; k < numeros.length; k++) {
      if (numeros[k].dia !== correlativos[correlativos.length - 1].dia + 1) break;
      correlativos.push(numeros[k]);
    }
    if (correlativos.length >= 28) {
      filaDias = i;
      dias = correlativos;
    }
  }
  if (filaDias === -1) {
    return { error: { rowNumber: 0, message: 'No se encontró la fila de días (1..31) de la grilla' } };
  }

  const filaEncabezado = aoa.findIndex((fila) => esFilaEncabezadoGrilla(fila ?? []));
  if (filaEncabezado === -1) {
    return { error: { rowNumber: 0, message: 'No se encontró el encabezado de la grilla (CADENA / REGION)' } };
  }

  const encabezado = aoa[filaEncabezado] ?? [];
  const buscar = (predicado: (n: string) => boolean): number =>
    encabezado.findIndex((celda) => predicado(normalizar(textoCelda(celda))));
  const colTienda = buscar((n) => n.startsWith('TIENDA'));
  if (colTienda === -1) {
    return {
      error: {
        rowNumber: filaEncabezado + 1,
        message: 'No se encontró la columna de tienda (TIENDA/DÍA SEMANA) en el encabezado de la grilla',
      },
    };
  }
  return {
    grilla: {
      filaEncabezado,
      colCadena: buscar((n) => n === 'CADENA'),
      colRegion: buscar((n) => n === 'REGION'),
      colTienda,
      colTT: buscar((n) => n === 'TT'),
      dias,
    },
  };
}

/** La fila TOTAL cierra el bloque. Se busca solo en las primeras columnas porque
 *  su parte numérica viene corrida una columna a la derecha. */
function esFilaTotal(fila: unknown[], colTienda: number): boolean {
  for (let c = 0; c <= colTienda + 1 && c < fila.length; c++) {
    if (normalizar(textoCelda(fila[c])).startsWith('TOTAL')) return true;
  }
  return false;
}

interface BloqueCrudo { tiendas: TiendaProgramada[]; filaInicio: number }

function leerBloques(aoa: unknown[][], g: Grilla, errores: ErrorCarga[]): BloqueCrudo[] {
  const bloques: BloqueCrudo[] = [];
  let actual: BloqueCrudo | null = null;

  for (let i = g.filaEncabezado + 1; i < aoa.length; i++) {
    const fila = aoa[i] ?? [];
    if (esFilaEncabezadoGrilla(fila)) continue; // encabezado repetido de otro bloque
    if (esFilaTotal(fila, g.colTienda)) {
      if (actual !== null) { bloques.push(actual); actual = null; }
      continue;
    }
    const nombre = textoCelda(fila[g.colTienda]);
    if (nombre === '') continue;

    const dias: number[] = [];
    for (const { dia, col } of g.dias) {
      const marca = textoCelda(fila[col]);
      if (marca !== '' && marca !== '0') dias.push(dia);
    }
    const totalDeclarado = g.colTT === -1 ? null : numeroCelda(fila[g.colTT]);
    if (totalDeclarado !== null && totalDeclarado !== dias.length) {
      errores.push({
        rowNumber: i + 1,
        message: `La tienda ${nombre} tiene ${dias.length} días marcados pero su columna TT dice ${totalDeclarado}`,
      });
    }
    if (actual === null) actual = { tiendas: [], filaInicio: i };
    actual.tiendas.push({
      nombre,
      cadena: g.colCadena === -1 ? '' : textoCelda(fila[g.colCadena]),
      region: g.colRegion === -1 ? '' : textoCelda(fila[g.colRegion]),
      dias,
      totalDeclarado,
    });
  }
  if (actual !== null) bloques.push(actual); // bloque final sin fila TOTAL
  return bloques;
}

// --- API -------------------------------------------------------------------

/** Lista las pestañas que sí son campañas. */
export function listarHojasCampania(data: ArrayBuffer): string[] {
  return leerLibro(data).SheetNames.filter((n) => !esIgnorada(n));
}

/** Lee una pestaña. Devuelve errores accionables en vez de lanzar.
 *  `rows` trae la campaña leída, o queda vacío si el archivo no se puede interpretar. */
export function parseHojaCampania(data: ArrayBuffer, nombreHoja: string): ParseResult<CampaniaProgramada> {
  const errores: ErrorCarga[] = [];
  const wb = leerLibro(data);
  const hoja = nombreReal(wb, nombreHoja);
  if (hoja === null) {
    return { rows: [], errors: [{ rowNumber: 0, message: `La pestaña «${nombreHoja}» no existe en el archivo` }] };
  }
  if (esIgnorada(hoja)) {
    return {
      rows: [],
      errors: [{ rowNumber: 0, message: `La pestaña «${hoja}» no es una campaña: es una hoja auxiliar del formato` }],
    };
  }

  const aoa = leerAoa(wb, hoja);
  const { grilla, error } = ubicarGrilla(aoa);
  if (grilla === undefined) return { rows: [], errors: [error as ErrorCarga] };

  const cabecera = leerCabecera(aoa, grilla.filaEncabezado);
  const ocurrencias = (campo: Campo): Ocurrencia[] => cabecera.get(campo) ?? [];
  const texto = (campo: Campo): string => textoCelda(ocurrencias(campo)[0]?.valor);
  const numero = (campo: Campo, i: number): number | null => {
    const o = ocurrencias(campo)[i];
    return o === undefined ? null : numeroCelda(o.valor);
  };

  const fechaOcurrencia = ocurrencias('fechaEjecucion')[0];
  const fecha = parseFechaEjecucion(fechaOcurrencia?.valor);
  if (fecha === null) {
    return {
      rows: [],
      errors: [{
        rowNumber: fechaOcurrencia === undefined ? 0 : fechaOcurrencia.fila + 1,
        message: 'No se pudo leer FECHA EJECUCION (dd/mm/aaaa): sin ella no se puede deducir el mes de la programación',
      }],
    };
  }

  const crudos = leerBloques(aoa, grilla, errores);
  if (crudos.length === 0) {
    return {
      rows: [],
      errors: [{ rowNumber: grilla.filaEncabezado + 1, message: 'La grilla no tiene filas de tienda' }],
    };
  }

  const bloques: BloqueTarifa[] = [];
  const faltantes: ErrorCarga[] = [];
  crudos.forEach((crudo, i) => {
    const tarifa = numero('presupuestoCaptador', i);
    if (tarifa === null) {
      faltantes.push({
        rowNumber: crudo.filaInicio + 1,
        message: `El bloque ${i + 1} (${crudo.tiendas.length} tiendas) no declara PRESUPUESTO DIARIO Captador en la cabecera`,
      });
      return;
    }
    bloques.push({
      rateAmountCaptador: tarifa,
      rateAmountSupervisor: numero('presupuestoSupervisor', i),
      totalDiasCaptadorDeclarado: numero('totalDiasCaptador', i),
      totalDiasSupervisorDeclarado: numero('totalDiasSupervisor', i),
      tiendas: crudo.tiendas,
    });
  });
  // Sin tarifa no hay campaign_schedule posible: se detiene en vez de cargar un cero.
  if (faltantes.length > 0) return { rows: [], errors: faltantes };

  const campania: CampaniaProgramada = {
    cuentaAnalitica: texto('cuentaAnalitica') || hoja,
    campania: texto('campania'),
    cliente: texto('cliente'),
    marca: texto('marca'),
    categoria: texto('categoria'),
    dinamica: texto('dinamica'),
    coordinador: texto('coordinador'),
    anio: fecha.anio,
    mes: fecha.mes,
    bloques,
  };
  return { rows: [campania], errors: errores };
}

/** Compara lo leído contra lo que declara el Excel. Una carga solo vale si cuadra. */
export function reconciliar(c: CampaniaProgramada): Reconciliacion {
  const porBloque = c.bloques.map((b, indice) => {
    const calculadas = b.tiendas.reduce((suma, t) => suma + t.dias.length, 0);
    const declaradas = b.totalDiasCaptadorDeclarado;
    return { indice, calculadas, declaradas, cuadra: declaradas !== null && calculadas === declaradas };
  });
  const jornadasCalculadas = porBloque.reduce((suma, b) => suma + b.calculadas, 0);
  // Si un solo bloque no declara su total, el total del Excel no es verificable.
  const sinDeclarar = porBloque.length === 0 || porBloque.some((b) => b.declaradas === null);
  const jornadasDeclaradas = sinDeclarar
    ? null
    : porBloque.reduce((suma, b) => suma + (b.declaradas ?? 0), 0);
  return {
    jornadasCalculadas,
    jornadasDeclaradas,
    cuadra: jornadasDeclaradas !== null && jornadasCalculadas === jornadasDeclaradas,
    porBloque,
  };
}

/** Verifica que el día 1 del mes caiga en el día de semana del encabezado de la grilla.
 *  Si no, la grilla está desalineada y hay que ABORTAR la carga: la lectura no falla,
 *  simplemente genera los días equivocados.
 *  `esperado` = lo que dice el calendario real; `real` = lo que escribió el Excel. */
export function validarAlineacionCalendario(data: ArrayBuffer, nombreHoja: string):
{ alineado: boolean; esperado: string; real: string } {
  const wb = leerLibro(data);
  const hoja = nombreReal(wb, nombreHoja);
  if (hoja === null || esIgnorada(hoja)) return { alineado: false, esperado: '', real: '' };

  const aoa = leerAoa(wb, hoja);
  const { grilla } = ubicarGrilla(aoa);
  if (grilla === undefined) return { alineado: false, esperado: '', real: '' };

  const cabecera = leerCabecera(aoa, grilla.filaEncabezado);
  const fecha = parseFechaEjecucion((cabecera.get('fechaEjecucion') ?? [])[0]?.valor);
  const colDiaUno = grilla.dias.find((d) => d.dia === 1)?.col;
  const real = colDiaUno === undefined ? '' : textoCelda((aoa[grilla.filaEncabezado] ?? [])[colDiaUno]);
  if (fecha === null) return { alineado: false, esperado: '', real };

  const esperado = NOMBRES_DIA[isoDiaSemana(fecha.anio, fecha.mes, 1) - 1];
  // El archivo abrevia y acentúa a su gusto ("Sáb.", "SABADO"): bastan tres letras.
  const clave = (t: string): string => normalizar(t).slice(0, 3);
  return { alineado: real !== '' && clave(real) === clave(esperado), esperado, real };
}

/** Traduce los días del mes a días de semana ISO, si el patrón es semanal regular.
 *  Devuelve null si los días no siguen un patrón semanal (ej. calendario estratégico),
 *  porque entonces no se puede representar como un campaign_schedule por días de semana. */
export function inferirDiasSemana(dias: number[], anio: number, mes: number): number[] | null {
  if (dias.length === 0) return null;
  const total = diasEnMes(anio, mes);
  if (dias.some((d) => !Number.isInteger(d) || d < 1 || d > total)) return null;

  const marcados = new Set(dias);
  const semana = new Set(dias.map((d) => isoDiaSemana(anio, mes, d)));
  // El patrón solo vale si TODOS los días del mes con esos días de semana están marcados.
  for (let d = 1; d <= total; d++) {
    if (semana.has(isoDiaSemana(anio, mes, d)) !== marcados.has(d)) return null;
  }
  return [...semana].sort((a, b) => a - b);
}
